import { Component } from '@theme/component';
 
const EARTH_RADIUS_KM = 6371;
const DEFAULT_ZOOM = 6;
const SEARCH_ZOOM = 12;
const MARKER_ZOOM = 15;
const MARKER_COLOR = '#023527';
 
/**
 * @param {number} deg
 * @returns {number}
 */
function toRad(deg) {
  return (deg * Math.PI) / 180;
}
 
/**
 * Haversine distance between two lat/lng points in km.
 * @param {number} lat1
 * @param {number} lng1
 * @param {number} lat2
 * @param {number} lng2
 * @returns {number}
 */
function haversineDistance(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
 
/**
 * @param {number} km
 * @returns {string}
 */
function formatDistance(km) {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}
 
/**
 * Build an SVG data URL for a numbered circle marker.
 * @param {number} index - 1-based index
 * @returns {string}
 */
function buildMarkerIcon(index) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 24 16 24s16-12 16-24C32 7.16 24.84 0 16 0Z" fill="${MARKER_COLOR}"/><circle cx="16" cy="15" r="10" fill="#fff"/><text x="16" y="19" text-anchor="middle" fill="${MARKER_COLOR}" font-size="${index > 9 ? 11 : 13}" font-weight="bold" font-family="Arial,sans-serif">${index}</text></svg>`;
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}
 
class StoreLocator extends Component {
  /** @type {any[]} */
  #stores = [];
 
  /** @type {any[]} */
  #filteredStores = [];
 
  /** @type {{ lat: number, lng: number } | null} */
  #userLocation = null;
 
  /** @type {google.maps.Map | null} */
  #map = null;
 
  /** @type {google.maps.Marker[]} */
  #markers = [];
 
  /** @type {google.maps.InfoWindow | null} */
  #infoWindow = null;
 
  /** @type {google.maps.places.Autocomplete | null} */
  #autocomplete = null;
 
  /** @type {google.maps.Geocoder | null} */
  #geocoder = null;
 
  /** @type {string | null} */
  #activeCardId = null;
 
  /** @type {AbortController} */
  #abortController = new AbortController();
 
  /** @type {HTMLDivElement} */
  #escapeEl = document.createElement('div');
 
  /** @type {Map<string, string> | null} */
  #typeLabels = null;
 
  connectedCallback() {
    super.connectedCallback();
    this.#loadGoogleMaps()
      .then(() => this.#loadStores())
      .catch(() => {});
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#clearMarkers();
    this.#map = null;
    this.#autocomplete = null;
    this.#geocoder = null;
    this.#infoWindow = null;
  }
 
  updatedCallback() {
    super.updatedCallback();
    this.#typeLabels = null;
    this.#clearMarkers();
    this.#map = null;
    this.#autocomplete = null;
    this.#geocoder = null;
    this.#infoWindow = null;
    this.#abortController = new AbortController();
    this.#loadGoogleMaps()
      .then(() => this.#loadStores())
      .catch(() => {});
  }
 
  /* --- Public event handlers (called via on:* declarative bindings) ------- */
 
  /** @param {KeyboardEvent} event */
  handleSearchKeydown(event) {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#triggerSearch();
  }
 
  handleSearchClick() {
    this.#triggerSearch();
  }
 
  /**
   * @param {{ query: string }} data
   */
  handleSuggestedSearch(data) {
    const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
    if (!input || !data?.query) return;
 
    input.value = data.query;
    this.#updateClearButton();
    this.#triggerSearch();
  }
 
  handleClear() {
    const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
    if (input) input.value = '';
    this.#userLocation = null;
    this.#filteredStores = [];
    this.#updateClearButton();
    this.#clearMarkers();
    this.#showEmptyState();
    this.#updateResultCount(0);
    if (this.#infoWindow) this.#infoWindow.close();
 
    const defaultLat = parseFloat(this.dataset.defaultLat || '40.4168');
    const defaultLng = parseFloat(this.dataset.defaultLng || '-3.7038');
    const defaultZoom = parseInt(this.dataset.defaultZoom || String(DEFAULT_ZOOM), 10);
    if (this.#map) this.#map.setCenter({ lat: defaultLat, lng: defaultLng });
    if (this.#map) this.#map.setZoom(defaultZoom);
    this.#showMapPlaceholder();
  }
 
  handleGeolocation() {
    if (!navigator.geolocation) return;
 
    const geoButton = this.refs.geoButton;
    if (geoButton) geoButton.setAttribute('aria-busy', 'true');
 
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.#userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
 
        const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
        if (input) input.value = this.#t('near_me');
        this.#updateClearButton();
 
        this.#searchAndRender();
        if (geoButton) geoButton.setAttribute('aria-busy', 'false');
      },
      () => {
        if (geoButton) geoButton.setAttribute('aria-busy', 'false');
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }
 
  /**
   * @param {{ storeId: string }} data
   * @param {Event} event
   */
  handleCardClick(data, event) {
    if (/** @type {HTMLElement} */ (event.composedPath()[0]).closest('a')) return;
    this.#focusStore(data.storeId);
  }
 
  /**
   * @param {{ storeId: string }} data
   * @param {KeyboardEvent} event
   */
  handleCardKeydown(data, event) {
    if (event.key !== 'Enter') return;
    this.#focusStore(data.storeId);
  }
 
  /* --- Private methods --------------------------------------------------- */
 
  /**
   * @returns {Promise<void>}
   */
  async #loadGoogleMaps() {
    if (window.google?.maps) return;
 
    const apiKey = this.dataset.googleMapsApiKey;
    if (!apiKey) {
      this.#showApiKeyError();
      return;
    }
 
    await new Promise((resolve, reject) => {
      const callbackName = '__storeLocatorGmapsInit';
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&callback=${callbackName}`;
      script.async = true;
      window[callbackName] = () => {
        delete window[callbackName];
        resolve(undefined);
      };
      script.onerror = () => {
        delete window[callbackName];
        reject(new Error('Failed to load Google Maps'));
      };
      document.head.appendChild(script);
    });
  }
 
  /**
   * @returns {Promise<void>}
   */
  async #loadStores() {
    const url = this.dataset.storesUrl;
    if (!url) return;
 
    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) return;
      const data = await response.json();
      this.#stores = (data.stores || data).map(s => ({
        ...s,
        lat: parseFloat(s.lat),
        lng: parseFloat(s.lng)
      }));
      this.#initMap();
      this.#initAutocomplete();
      this.#showEmptyState();
    } catch {
      /* fetch aborted or network error */
    }
  }
 
  #initMap() {
    if (!window.google?.maps) return;
 
    const mapEl = this.refs.map;
    if (!mapEl) return;
 
    const defaultLat = parseFloat(this.dataset.defaultLat || '40.4168');
    const defaultLng = parseFloat(this.dataset.defaultLng || '-3.7038');
    const defaultZoom = parseInt(this.dataset.defaultZoom || String(DEFAULT_ZOOM), 10);
 
    this.#map = new google.maps.Map(/** @type {HTMLElement} */ (mapEl), {
      center: { lat: defaultLat, lng: defaultLng },
      zoom: defaultZoom,
      gestureHandling: 'cooperative',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });
 
    this.#infoWindow = new google.maps.InfoWindow();
    this.#geocoder = new google.maps.Geocoder();
  }
 
  #initAutocomplete() {
    if (!window.google?.maps?.places) return;
 
    const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
    if (!input) return;
 
    this.#autocomplete = new google.maps.places.Autocomplete(input, {
      componentRestrictions: { country: ['es', 'ad'] },
      fields: ['geometry', 'name'],
      types: ['geocode'],
    });
 
    this.#autocomplete.addListener('place_changed', () => {
      const place = this.#autocomplete?.getPlace();
      if (place?.geometry?.location) {
        this.#userLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        this.#updateClearButton();
        this.#searchAndRender();
      }
    });
 
    input.addEventListener('input', () => this.#updateClearButton(), {
      signal: this.#abortController.signal,
    });
  }
 
  #triggerSearch() {
    const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
    const query = input?.value?.trim();
 
    if (!query) {
      this.handleClear();
      return;
    }
 
    const place = this.#autocomplete?.getPlace();
    if (place?.geometry?.location) {
      this.#userLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
      this.#searchAndRender();
      return;
    }
 
    if (this.#geocoder) {
      this.#geocoder.geocode(
        { address: query, componentRestrictions: { country: ['ES', 'AD'] } },
        (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            this.#userLocation = {
              lat: location.lat(),
              lng: location.lng(),
            };
            this.#searchAndRender();
          } else {
            this.#showNoResults();
          }
        }
      );
    }
  }
 
  #searchAndRender() {
    if (!this.#userLocation) return;

    const radiusKm = parseInt(this.dataset.searchRadiusKm || '50', 10);
    const maxResults = parseInt(this.dataset.maxResults || '40', 10);

    const { lat, lng } = this.#userLocation;

    this.#filteredStores = this.#stores
      .map((s) => ({
        ...s,
        distance: haversineDistance(lat, lng, s.lat, s.lng),
      }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, maxResults);

    this.#renderStoreList();
    this.#updateMarkers();
    this.#fitBounds();
  }
 
  #renderStoreList() {
    const listEl = this.refs.storeList;
    if (!listEl) return;
 
    this.#updateResultCount(this.#filteredStores.length);
 
    if (this.#filteredStores.length === 0) {
      this.#showNoResults();
      return;
    }
 
    let html = '';
    for (let i = 0; i < this.#filteredStores.length; i++) {
      html += this.#buildStoreCardHtml(this.#filteredStores[i], i + 1);
    }
    listEl.innerHTML = html;
    this.#hideMapPlaceholder();
  }
 
  /**
   * @param {any} store
   * @param {number} index - 1-based
   * @returns {string}
   */
  #buildStoreCardHtml(store, index) {
    const typeLabel = this.#getTypeLabel(store.type);
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
    const distanceHtml = store.distance != null
      ? `<span class="store-locator__distance">${formatDistance(store.distance)}</span>`
      : '';
 
    let hoursHtml = '';
    if (store.hours) {
      hoursHtml = '<div class="store-locator__hours">';
      if (store.hours.mon_fri) hoursHtml += `<span>${this.#t('hours_weekday')}: ${this.#escapeHtml(store.hours.mon_fri)}</span>`;
      if (store.hours.sat) hoursHtml += `<span>${this.#t('hours_saturday')}: ${this.#escapeHtml(store.hours.sat)}</span>`;
      if (store.hours.sun) hoursHtml += `<span>${this.#t('hours_sunday')}: ${this.#escapeHtml(store.hours.sun)}</span>`;
      hoursHtml += '</div>';
    }
 
    let servicesHtml = '';
    if (store.services?.length > 0) {
      servicesHtml = '<div class="store-locator__services">';
      for (const service of store.services) {
        servicesHtml += `<span class="store-locator__service">${this.#t(`service_${service}`)}</span>`;
      }
      servicesHtml += '</div>';
    }
 
    return `
      <article class="store-locator__card" data-store-id="${store.id}" tabindex="0" role="listitem"
        aria-label="${this.#t('view_on_map')}: ${this.#escapeHtml(store.name)}"
        on:click="/handleCardClick?storeId=${store.id}"
        on:keydown="/handleCardKeydown?storeId=${store.id}">
        <div class="store-locator__card-header">
          <span class="store-locator__card-badge">${index}</span>
          <div class="store-locator__card-title">
            <h3 class="store-locator__card-name">${this.#escapeHtml(store.name)}</h3>
          </div>
          <div class="store-locator__card-meta">
            ${typeLabel ? `<span class="pills__pill store-locator__badge">${typeLabel}</span>` : ''}
            ${distanceHtml}
          </div>
        </div>
        <div class="store-locator__card-body">
          <p class="store-locator__row">
            <svg class="store-locator__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M8 1C5.24 1 3 3.24 3 6c0 3.75 5 9 5 9s5-5.25 5-9c0-2.76-2.24-5-5-5Zm0 6.75a1.75 1.75 0 1 1 0-3.5 1.75 1.75 0 0 1 0 3.5Z" fill="currentColor"/>
            </svg>
            ${this.#escapeHtml(store.address)}${store.postal_code ? `, ${this.#escapeHtml(store.postal_code)}` : ''} ${this.#escapeHtml(store.city)}
          </p>
          ${store.phone ? `
          <p class="store-locator__row">
            <svg class="store-locator__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M13.6 10.83c-.82-.78-1.83-.78-2.64 0l-.63.63c-.52.52-1.06.38-1.86-.22a12 12 0 0 1-2.21-2.1c-.76-.93-.86-1.42-.27-2.01l.56-.56c.82-.82.82-1.82 0-2.64L5.77 3.1c-.82-.82-1.82-.82-2.64 0l-.46.47c-1.47 1.53-.96 3.83.63 5.88a16.14 16.14 0 0 0 3.44 3.3c2.03 1.5 4.19 1.96 5.72.43l.45-.45c.82-.82.82-1.83 0-2.65l-.78-.82-.53-.43Z" fill="currentColor"/>
            </svg>
            <a href="tel:${store.phone}" class="store-locator__phone-link">${this.#escapeHtml(store.phone)}</a>
          </p>` : ''}
          ${hoursHtml}
          ${servicesHtml}
        </div>
        <div class="store-locator__card-footer">
          <a href="${directionsUrl}" target="_blank" rel="noopener" class="store-locator__link">
            <svg class="store-locator__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M14.7 7.29 8.71 1.3a1.003 1.003 0 0 0-1.42 0L1.3 7.29c-.39.39-.39 1.02 0 1.41l5.99 6a1.003 1.003 0 0 0 1.42 0l5.99-6c.39-.39.39-1.02 0-1.41ZM9 10v2H7V9.5L5.5 8 7 6.5V5h2v2l1.5 1.5L9 10Z" fill="currentColor"/>
            </svg>
            ${this.#t('get_directions')}
          </a>
        </div>
      </article>
    `;
  }
 
  #updateMarkers() {
    this.#clearMarkers();
    if (!this.#map) return;
 
    for (let i = 0; i < this.#filteredStores.length; i++) {
      const store = this.#filteredStores[i];
      if (!store.lat || !store.lng) continue;
 
      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map: this.#map,
        icon: {
          url: buildMarkerIcon(i + 1),
          scaledSize: new google.maps.Size(32, 40),
          anchor: new google.maps.Point(16, 40),
        },
        title: store.name,
      });
 
      marker.addListener('click', () => {
        this.#openInfoWindow(store, marker);
        this.#highlightCard(store.id);
      });
 
      this.#markers.push(marker);
    }
  }
 
  #clearMarkers() {
    for (const marker of this.#markers) {
      marker.setMap(null);
    }
    this.#markers = [];
  }
 
  #fitBounds() {
    if (!this.#map || this.#markers.length === 0) return;
 
    if (this.#markers.length === 1) {
      const pos = this.#markers[0].getPosition();
      if (pos) {
        this.#map.setCenter(pos);
        this.#map.setZoom(MARKER_ZOOM);
      }
      return;
    }
 
    const bounds = new google.maps.LatLngBounds();
    for (const marker of this.#markers) {
      const pos = marker.getPosition();
      if (pos) bounds.extend(pos);
    }
    this.#map.fitBounds(bounds, 40);
  }
 
  /**
   * @param {any} store
   * @param {google.maps.Marker} marker
   */
  #openInfoWindow(store, marker) {
    if (!this.#infoWindow || !this.#map) return;
 
    const typeLabel = this.#getTypeLabel(store.type);
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.lng}`;
 
    let html = '<div class="store-locator__infowindow">';
    html += `<strong class="store-locator__infowindow-name">${this.#escapeHtml(store.name)}</strong>`;
    if (typeLabel) {
      html += `<span class="pills__pill store-locator__badge store-locator__infowindow-badge">${typeLabel}</span>`;
    }
    html += `<p class="store-locator__infowindow-address">${this.#escapeHtml(store.address)}<br>${this.#escapeHtml(store.postal_code || '')} ${this.#escapeHtml(store.city)}, ${this.#escapeHtml(store.country)}</p>`;
    if (store.phone) {
      html += `<p class="store-locator__infowindow-phone"><a href="tel:${store.phone}" class="store-locator__phone-link store-locator__phone-link--primary">${this.#escapeHtml(store.phone)}</a></p>`;
    }
    html += `<a href="${directionsUrl}" target="_blank" rel="noopener" class="store-locator__link">${this.#t('get_directions')}</a>`;
    html += '</div>';
 
    this.#infoWindow.setContent(html);
    this.#infoWindow.open(this.#map, marker);
  }
 
  /**
   * @param {string | undefined} storeId
   */
  #focusStore(storeId) {
    if (!storeId || !this.#map) return;
 
    const storeIndex = this.#filteredStores.findIndex((s) => s.id === storeId);
    if (storeIndex === -1) return;
 
    const store = this.#filteredStores[storeIndex];
    const marker = this.#markers[storeIndex];
 
    this.#map.panTo({ lat: store.lat, lng: store.lng });
    this.#map.setZoom(MARKER_ZOOM);
 
    if (marker) {
      this.#openInfoWindow(store, marker);
    }
 
    this.#highlightCard(storeId);
 
    const mapRef = this.refs.map;
    if (mapRef) mapRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
 
  /**
   * @param {string} storeId
   */
  #highlightCard(storeId) {
    if (this.#activeCardId) {
      const prev = this.querySelector(`[data-store-id="${this.#activeCardId}"]`);
      if (prev) prev.classList.remove('store-locator__card--active');
    }
 
    this.#activeCardId = storeId;
    const card = this.querySelector(`[data-store-id="${storeId}"]`);
    if (card) {
      card.classList.add('store-locator__card--active');
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
 
  /**
   * @param {number} count
   */
  #updateResultCount(count) {
    const countEl = this.refs.resultCount;
    if (countEl) {
      countEl.textContent = count > 0
        ? this.#t('results_count').replace('{{ count }}', String(count))
        : '';
    }
  }
 
  #updateClearButton() {
    const input = /** @type {HTMLInputElement | null} */ (this.refs.searchInput);
    const clearBtn = this.refs.clearButton;
    if (clearBtn) {
      clearBtn.hidden = !input?.value;
    }
  }
 
  #showEmptyState() {
    const listEl = this.refs.storeList;
    if (!listEl) return;
 
    this.#showMapPlaceholder();
 
    const suggestedCities = [
      this.#t('popular_city_madrid'),
      this.#t('popular_city_barcelona'),
      this.#t('popular_city_valencia'),
      this.#t('popular_city_sevilla'),
    ].filter(Boolean);
 
    const suggestedCitiesHtml = suggestedCities.length > 0
      ? `
        <div class="store-locator__empty-suggestions">
          <p class="store-locator__empty-hint">${this.#t('popular_cities_label')}</p>
          <div class="store-locator__empty-chip-row">
            ${suggestedCities
              .map(
                (city) =>
                  `<button class="store-locator__empty-chip" type="button" on:click="/handleSuggestedSearch?query=${encodeURIComponent(city)}">${this.#escapeHtml(city)}</button>`
              )
              .join('')}
          </div>
        </div>
      `
      : '';
 
    listEl.innerHTML = `
      <div class="store-locator__empty">
        <svg class="store-locator__empty-icon" width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
          <path d="M24 4C16.27 4 10 10.27 10 18c0 11.25 14 26 14 26s14-14.75 14-26c0-7.73-6.27-14-14-14Zm0 19a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" fill="currentColor" opacity="0.2"/>
          <path d="M24 4C16.27 4 10 10.27 10 18c0 11.25 14 26 14 26s14-14.75 14-26c0-7.73-6.27-14-14-14Zm0 19a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" stroke="currentColor" stroke-width="1.5" fill="none"/>
        </svg>
        <p class="store-locator__empty-title">${this.#t('empty_state')}</p>
        <p class="store-locator__empty-hint">${this.#t('empty_hint')}</p>
        ${suggestedCitiesHtml}
      </div>
    `;
  }
 
  #showNoResults() {
    const listEl = this.refs.storeList;
    if (!listEl) return;
 
    this.#updateResultCount(0);
    this.#showMapPlaceholder();
 
    listEl.innerHTML = `
      <div class="store-locator__empty">
        <svg class="store-locator__empty-icon" width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
          <path d="M24 4C16.27 4 10 10.27 10 18c0 11.25 14 26 14 26s14-14.75 14-26c0-7.73-6.27-14-14-14Zm0 19a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" fill="currentColor" opacity="0.2"/>
          <path d="M24 4C16.27 4 10 10.27 10 18c0 11.25 14 26 14 26s14-14.75 14-26c0-7.73-6.27-14-14-14Zm0 19a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" stroke="currentColor" stroke-width="1.5" fill="none"/>
        </svg>
        <p class="store-locator__empty-title">${this.#t('no_results')}</p>
        <p class="store-locator__empty-hint">${this.#t('empty_hint')}</p>
      </div>
    `;
  }
 
  #showApiKeyError() {
    const mapEl = this.refs.map;
    const errorEl = this.refs.mapError;
    if (mapEl) mapEl.hidden = true;
    this.#hideMapPlaceholder();
    if (errorEl) errorEl.hidden = false;
  }
 
  #showMapPlaceholder() {
    const placeholder = this.refs.mapPlaceholder;
    if (placeholder) placeholder.hidden = false;
  }
 
  #hideMapPlaceholder() {
    const placeholder = this.refs.mapPlaceholder;
    if (placeholder) placeholder.hidden = true;
  }
 
  /**
   * @param {string} type
   * @returns {string}
   */
  #getTypeLabel(type) {
    if (!type) return '';
    if (!this.#typeLabels) {
      this.#typeLabels = new Map([
        ['pharmacy', this.#t('type_pharmacy')],
        ['salon', this.#t('type_salon')],
        ['flagship', this.#t('type_flagship')],
      ]);
    }
    return this.#typeLabels.get(type) ?? type;
  }
 
  /**
   * @param {string} key
   * @returns {string}
   */
  #t(key) {
    return this.dataset[`t${key.charAt(0).toUpperCase()}${key.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}`] || key;
  }
 
  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    if (!str) return '';
    this.#escapeEl.textContent = str;
    return this.#escapeEl.innerHTML;
  }
}
 
customElements.define('store-locator-component', StoreLocator);
 
export default StoreLocator;
 